import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { ChevronDown, Calendar, Check } from 'lucide-react';

const CustomDatePicker = forwardRef(({
  value,
  onChange,
  label,
  required = false,
  name,
  className = "",
  validationError,
  title,
  maxYear = new Date().getFullYear() + 200,
  minYear = new Date().getFullYear() - 200
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false)
  }));
  const [tempDate, setTempDate] = useState({
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const containerRef = useRef(null);
  const scrollerRefs = {
    day: useRef(null),
    month: useRef(null),
    year: useRef(null)
  };
  const isSyncingRef = useRef(false);
  const isReadyRef = useRef(false);

  const months = React.useMemo(() => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ], []);

  const years = React.useMemo(() => 
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i)
  , [maxYear, minYear]);

  // Initialize from value or current date
  useEffect(() => {
    let date;
    if (value) {
      // Parse YYYY-MM-DD as local date to avoid UTC timezone shifts
      const [y, m, d] = value.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date();
    }

    if (!isNaN(date.getTime())) {
      setTempDate({
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      });
      
      // Removed auto-onChange initialization as it triggers unwanted side-effects on form resets
    }
  }, [value, isOpen]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Give the scrollers time to mount and sync before allowing manual scroll updates
      isReadyRef.current = false;
      const timer = setTimeout(() => {
        isReadyRef.current = true;
      }, 400);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        clearTimeout(timer);
      };
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();

  const handleScroll = (type, list, itemHeight = 40) => {
    if (isSyncingRef.current || !isReadyRef.current) return;
    const scroller = scrollerRefs[type].current;
    if (!scroller) return;

    const scrollPos = scroller.scrollTop;
    const index = Math.round(scrollPos / itemHeight);
    const newValue = list[index];

    if (newValue && newValue !== tempDate[type]) {
      setTempDate(prev => {
        const updated = { ...prev, [type]: newValue };
        // Validating day if month/year changes
        const maxDays = getDaysInMonth(updated.year, updated.month);
        if (updated.day > maxDays) updated.day = maxDays;
        return updated;
      });
    }
  };

  const dragRef = useRef({ isDragging: false, startY: 0, scrollTop: 0, type: null });

  const handleMouseDown = (e, type) => {
    dragRef.current = {
      isDragging: true,
      startY: e.pageY - scrollerRefs[type].current.offsetTop,
      scrollTop: scrollerRefs[type].current.scrollTop,
      type
    };
    scrollerRefs[type].current.style.cursor = 'grabbing';
    scrollerRefs[type].current.style.scrollSnapType = 'none'; // Disable snap during drag
  };

  const handleMouseMove = (e) => {
    const { isDragging, startY, scrollTop, type } = dragRef.current;
    if (!isDragging || !type) return;

    e.preventDefault();
    const y = e.pageY - scrollerRefs[type].current.offsetTop;
    const walk = (y - startY) * 1.5; // Drag speed multiplier
    scrollerRefs[type].current.scrollTop = scrollTop - walk;
  };

  const handleMouseUp = (type) => {
    if (!dragRef.current.isDragging) return;
    
    dragRef.current.isDragging = false;
    const scroller = scrollerRefs[type || dragRef.current.type].current;
    if (scroller) {
      scroller.style.cursor = 'grab';
      scroller.style.scrollSnapType = 'y mandatory'; // Re-enable snap
    }
  };

  const handleItemClick = (type, value) => {
    if (dragRef.current.isDragging) return; // Don't trigger click if we were dragging
    setTempDate(prev => {
      const updated = { ...prev, [type]: value };
      const maxDays = getDaysInMonth(updated.year, updated.month);
      if (updated.day > maxDays) updated.day = maxDays;
      return updated;
    });
  };

  const handleKeyDown = (e, type, list) => {
    const currentIndex = list.indexOf(tempDate[type]);
    let nextIndex = currentIndex;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = Math.max(0, currentIndex - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = Math.min(list.length - 1, currentIndex + 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      confirmDate();
      return;
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }

    if (nextIndex !== currentIndex) {
      const newValue = list[nextIndex];
      setTempDate(prev => {
        const updated = { ...prev, [type]: newValue };
        const maxDays = getDaysInMonth(updated.year, updated.month);
        if (updated.day > maxDays) updated.day = maxDays;
        return updated;
      });
    }
  };

  const syncScrollPositions = () => {
    const itemHeight = 36;
    if (dragRef.current.isDragging) return;

    isSyncingRef.current = true;
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (scrollerRefs.day.current) {
        scrollerRefs.day.current.scrollTop = (tempDate.day - 1) * itemHeight;
      }
      if (scrollerRefs.month.current) {
        scrollerRefs.month.current.scrollTop = (tempDate.month - 1) * itemHeight;
      }
      if (scrollerRefs.year.current) {
        const yearIndex = years.indexOf(tempDate.year);
        if (yearIndex !== -1) {
          scrollerRefs.year.current.scrollTop = yearIndex * itemHeight;
        }
      }
      
      // Release sync lock after a longer delay to ensure all scroll events are processed
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 150);
    });
  };

  // Sync scroll whenever tempDate changes (from any interaction)
  useEffect(() => {
    if (isOpen) syncScrollPositions();
  }, [tempDate, isOpen]);

  const confirmDate = () => {
    // Generate YYYY-MM-DD manually to avoid timezone shifts from toISOString()
    const y = tempDate.year;
    const m = String(tempDate.month).padStart(2, '0');
    const d = String(tempDate.day).padStart(2, '0');
    const formattedDate = `${y}-${m}-${d}`;
    onChange({ target: { name, value: formattedDate } });
    setIsOpen(false);
  };

  const formatDateDisplay = () => {
    if (!value) return "Select Date";
    const date = new Date(value);
    return `${date.getDate()} ${months[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;
  };

  return (
    <div className={`relative flex flex-col space-y-2 ${className}`} ref={containerRef}>
      {label && (
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2.5 bg-white border rounded-2xl transition-all duration-300 ${
          isOpen ? 'ring-2 ring-blue-500 border-blue-500 shadow-lg' : 'border-slate-100 hover:border-slate-300 premium-shadow-sm'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <Calendar size={16} className={value ? "text-blue-500" : "text-slate-400"} />
          <span className={`font-bold text-sm ${value ? "text-slate-700" : "text-slate-400"}`}>
            {formatDateDisplay()}
          </span>
        </div>
        <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
      </button>

      {/* Popup Picker Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-[9998]" onClick={() => setIsOpen(false)} />
          
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90%] max-w-[320px] bg-white rounded-[2rem] p-5 premium-shadow border border-slate-100 animate-scaleIn select-none">
            {title && (
              <div className="text-center mb-4">
                <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider">{title}</h3>
                <div className="h-0.5 w-8 bg-blue-500 mx-auto mt-1 rounded-full" />
              </div>
            )}
            <div className="flex gap-1.5 h-40 relative overflow-hidden">
              {/* Selection Overlay */}
              <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-9 bg-blue-50/50 rounded-lg pointer-events-none -z-10 border-y border-blue-100" />
              
              {/* Day Column */}
              <div 
                ref={scrollerRefs.day}
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, 'day', Array.from({ length: getDaysInMonth(tempDate.year, tempDate.month) }, (_, i) => i + 1))}
                onScroll={() => handleScroll('day', Array.from({ length: 31 }, (_, i) => i + 1), 36)}
                onMouseDown={(e) => handleMouseDown(e, 'day')}
                onMouseMove={handleMouseMove}
                onMouseUp={() => handleMouseUp('day')}
                onMouseLeave={() => handleMouseUp('day')}
                  className="flex-1 overflow-y-auto snap-y snap-mandatory no-scrollbar outline-none focus:bg-slate-50/30 rounded-lg transition-colors cursor-grab active:cursor-grabbing"
              >
                <div className="py-[62px] pointer-events-none">
                  {Array.from({ length: getDaysInMonth(tempDate.year, tempDate.month) }, (_, i) => i + 1).map(d => (
                    <div 
                      key={d} 
                      onClick={() => handleItemClick('day', d)}
                      className={`h-9 flex items-center justify-center snap-center font-bold transition-all ${tempDate.day === d ? 'text-blue-600 text-base scale-110' : 'text-slate-400 text-xs'}`}
                    >
                      {d.toString().padStart(2, '0')}
                    </div>
                  ))}
                </div>
              </div>

              {/* Month Column */}
              <div 
                ref={scrollerRefs.month}
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, 'month', Array.from({ length: 12 }, (_, i) => i + 1))}
                onScroll={() => handleScroll('month', Array.from({ length: 12 }, (_, i) => i + 1), 36)}
                onMouseDown={(e) => handleMouseDown(e, 'month')}
                onMouseMove={handleMouseMove}
                onMouseUp={() => handleMouseUp('month')}
                onMouseLeave={() => handleMouseUp('month')}
                className="flex-[1.2] overflow-y-auto snap-y snap-mandatory no-scrollbar outline-none focus:bg-slate-50/30 rounded-lg transition-colors cursor-grab active:cursor-grabbing"
              >
                <div className="py-[62px] pointer-events-none">
                  {months.map((m, i) => (
                    <div 
                      key={m} 
                      onClick={() => handleItemClick('month', i + 1)}
                      className={`h-9 flex items-center justify-center snap-center font-bold transition-all ${tempDate.month === (i + 1) ? 'text-blue-600 text-base scale-110' : 'text-slate-400 text-xs'}`}
                    >
                      {m.slice(0, 3)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Year Column */}
              <div 
                ref={scrollerRefs.year}
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, 'year', years)}
                onScroll={() => handleScroll('year', years, 36)}
                onMouseDown={(e) => handleMouseDown(e, 'year')}
                onMouseMove={handleMouseMove}
                onMouseUp={() => handleMouseUp('year')}
                onMouseLeave={() => handleMouseUp('year')}
                className="flex-[1.1] overflow-y-auto snap-y snap-mandatory no-scrollbar outline-none focus:bg-slate-50/30 rounded-lg transition-colors cursor-grab active:cursor-grabbing"
              >
                <div className="py-[62px] pointer-events-none">
                  {years.map(y => (
                    <div 
                      key={y} 
                      onClick={() => handleItemClick('year', y)}
                      className={`h-9 flex items-center justify-center snap-center font-bold transition-all ${tempDate.year === y ? 'text-blue-600 text-base scale-110' : 'text-slate-400 text-xs'}`}
                    >
                      {y}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 py-2.5 bg-slate-50 text-slate-500 rounded-xl font-bold text-sm hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDate}
                className="flex-[2] py-2.5 primary-gradient-bg text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200/50 flex items-center justify-center gap-2 hover:opacity-90 transition-all active:scale-95"
              >
                <Check size={16} />
                Apply Date
              </button>
            </div>
          </div>
        </>
      )}

      {validationError && (
        <p className="mt-1 text-[10px] font-bold text-red-500 ml-2 animate-shake">{validationError}</p>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes scaleIn {
          from { opacity: 0; transform: translate(-50%, -45%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        .animate-scaleIn { animation: scaleIn 0.2s ease-out forwards; }
      `}} />
    </div>
  );
});

export default CustomDatePicker;
